import { Navigate } from 'react-router';
import { LandingPagesNavbar, LandingPagesFooter, ScrollReveal } from '@podus/ui';
import { useAuth } from '@/context/AuthContext';
import MultidoorFeatures from '@/components/landing/MultidoorFeatures';
import WhyMultidoor from '@/components/landing/WhyMultidoor';
import Audience from '@/components/landing/Audience';
import Testimonials from '@/components/landing/Testimonials';
import FAQ from '@/components/landing/FAQ';

export default function Home() {
    const { isLoggedIn, openSignup } = useAuth();

    if (isLoggedIn) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="page-scroll-root bg-white">
            <LandingPagesNavbar product="podus-multidoor" />

            <main className="overflow-x-clip">
                {/* ── Hero ─ */}
                <section className="mx-auto max-w-[1920px] px-4 pt-4 sm:pt-6 lg:px-10 lg:pt-8 xl:px-16">
                    <div
                        className="relative overflow-hidden rounded-2xl bg-[#F3F3F3] bg-no-repeat xl:flex xl:min-h-[calc(100dvh-120px)] xl:items-center"
                        style={{
                            backgroundImage: "url('/assets/cube_pattern.svg')",
                            backgroundPosition: 'center bottom',
                            backgroundSize: '100% auto',
                        }}
                    >
                        <div className="relative grid w-full gap-6 px-5 pt-10 pb-8 sm:px-10 sm:pt-14 sm:pb-12 lg:px-14 lg:py-20 xl:grid-cols-12 xl:items-center xl:gap-6 xl:px-20 xl:py-24">
                            {/* Left — copy */}
                            <div className="relative z-10 xl:col-span-7">
                                <ScrollReveal variant="fade-up" duration={800}>
                                    <h1>
                                        <span className="block text-[clamp(1.875rem,4.4vw,5rem)] font-extrabold leading-[1] text-[#AEAEAE]">
                                            Resolve Disputes
                                        </span>
                                        <span className="mt-2 block text-[clamp(2.75rem,6.8vw,7.5rem)] font-normal italic leading-[1.05] text-secondary">
                                            Smarter &amp; Fairer
                                        </span>
                                    </h1>
                                </ScrollReveal>

                                <ScrollReveal variant="fade-up" delay={200} duration={700}>
                                    <div className="mt-8 flex items-start gap-4 sm:mt-10">
                                        <span className="mt-1 inline-block h-10 w-[8px] shrink-0 bg-primary sm:h-12 2xl:h-14" />
                                        <p className="text-[clamp(0.875rem,0.9vw,1.125rem)] leading-[1.65] text-[#4E4D4D]">
                                            Podus Multidoor is a structured, multi-path dispute resolution platform that
                                            connects people, institutions, and professionals to the right resolution
                                            process—without confusion, delay, or bias.
                                        </p>
                                    </div>
                                </ScrollReveal>

                                <ScrollReveal variant="fade-up" delay={350} duration={700}>
                                    <button
                                        type="button"
                                        onClick={() => (isLoggedIn ? undefined : openSignup())}
                                        className="mt-8 w-[200px] inline-flex items-center justify-center rounded-full bg-secondary px-8 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-secondary-400 sm:mt-10 sm:px-10 sm:py-3.5"
                                    >
                                        Create Case
                                    </button>
                                </ScrollReveal>
                            </div>

                            {/* Right — gavel illustration */}
                            <div className="relative xl:col-span-5">
                                <ScrollReveal variant="fade-up" delay={150} duration={900}>
                                    <img
                                        src="/assets/gavel_skeleton.svg"
                                        alt="Wireframe gavel"
                                        className="pointer-events-none mx-auto w-full max-w-[360px] select-none sm:max-w-[480px] lg:max-w-[560px] xl:ml-auto xl:mr-0 xl:w-full xl:max-w-[640px] 2xl:max-w-[760px]"
                                    />
                                </ScrollReveal>
                            </div>
                        </div>
                    </div>
                </section>

                <div className="mx-auto max-w-[1920px]">
                    <MultidoorFeatures />
                    <WhyMultidoor />
                    <Audience />
                    <Testimonials />
                    <FAQ />
                </div>
            </main>

            <LandingPagesFooter />
        </div>
    );
}
